import clsx from 'clsx';
import { useState } from 'react';

import type { Color } from '../../../types/colors';
import type { Size } from '../../../types/layout';
import { SgComponentIcon } from '../../display/ComponentIcon';
import { SgFormattedMessage } from '../../SgFormattedMessage';
import type { SgPickerItem } from './types';
import { getItemStyle } from './utils';

/**
 * DropdownItemButton - A versatile dropdown item component with advanced color and interaction management
 *
 * COLOR & STYLE MANAGEMENT STRATEGY:
 * =====================================
 *
 * This component implements a sophisticated color override system that handles multiple interaction states:
 *
 * 1. INTERACTION STATE DETECTION:
 *    - Mouse hover vs keyboard navigation are tracked separately via `isHoveredByMouse`
 *    - This prevents visual conflicts when users switch between mouse and keyboard
 *    - Keyboard highlights only apply when NOT hovering with mouse
 *
 * 2. STYLE PRIORITY HIERARCHY (highest to lowest):
 *    - Disabled state (overrides everything)
 *    - Selected state (for regular items)
 *    - Keyboard highlight (accessibility focus)
 *    - Mouse hover (via CSS)
 *    - Default state
 *
 * 3. COLOR THEMING:
 *    - Uses `getItemStyle()` utility to apply theme colors dynamically
 *    - Supports color prop override (primary, secondary, success, etc.)
 *    - CSS classes (btn-flat-{color}) provide base styling
 *    - Inline styles override for specific states (selected, highlighted)
 *
 * 4. ACCESSIBILITY CONSIDERATIONS:
 *    - Keyboard navigation gets distinct visual feedback
 *    - Focus states are clearly differentiated from hover states
 *    - Disabled items maintain proper contrast ratios
 *    - Screen reader compatible with proper button semantics
 */

export interface DropdownItemButtonProps<T extends string | number> {
  item: SgPickerItem<T>;
  index: number;
  isHighlighted: boolean;
  getItemProps: (options: { index: number; item: SgPickerItem<T> }) => Record<string, unknown>;
  color: Color;
  size: Size;
  /** Props specific to item type */
  itemType: 'selectAll' | 'createCustom' | 'regular';
  /** Props for selectAll items */
  areAllSelected?: boolean;
  selectedItems?: SgPickerItem<T>[];
  itemSelection?: 'single' | 'multiple';
  selectedItem?: SgPickerItem<T> | null;
  /** Props for createCustom items */
  inputValue?: string;
  createCustomItemButtonLabel?: string;
}

export function DropdownItemButton<T extends string | number>({
  item,
  index,
  isHighlighted,
  getItemProps,
  color,
  size,
  itemType,
  areAllSelected = false,
  selectedItems = [],
  itemSelection = 'single',
  selectedItem,
  inputValue = '',
  createCustomItemButtonLabel = 'Create',
}: DropdownItemButtonProps<T>) {
  /**
   * State to differentiate between mouse hover and keyboard navigation.
   * This is crucial for proper style management - we want different visual feedback
   * for mouse interactions vs keyboard navigation to improve accessibility.
   */
  const [isHoveredByMouse, setIsHoveredByMouse] = useState(false);

  const baseClassName =
    'fw-normal font-weight-normal h-auto d-flex align-items-center btn btn-block text-left text-start mt-0 border border-opacity-20 border-alpha-sm border-start-0 border-left-0 border-top-0 border-end-0 border-right-0 btn-flat-primary';
  const isDisabled = Boolean(item.disabled);

  /**
   * Generates common props for button elements with dynamic style management.
   *
   * Color & Style Override Strategy:
   * - Uses getItemStyle() to compute dynamic styles based on state (selected, highlighted, disabled)
   * - isKeyboardHighlight differentiates mouse hover from keyboard navigation for accessibility
   * - Mouse events (onMouseEnter/Leave) track mouse interaction to override keyboard highlighting
   * - Style priority: disabled > selected > keyboard highlight > mouse hover > default
   *
   * @param isSelectedForProps - Whether to apply selected styles (used for regular items only)
   */
  const getCommonProps = (isSelectedForProps = false) => {
    const isSelectedForStyle = itemType === 'regular' ? isSelectedForProps : false;

    /**
     * Keyboard highlighting should only apply when:
     * 1. Item is highlighted by downshift (isHighlighted = true)
     * 2. User is NOT currently hovering with mouse (prevents conflict)
     */
    const isKeyboardHighlight = isHighlighted && !isHoveredByMouse;

    return {
      type: 'button' as const,
      ...getItemProps({
        index,
        item,
      }),
      style: {
        cursor: isDisabled ? 'default' : 'pointer',
        /**
         * getItemStyle handles the complex color override logic:
         * - Applies theme colors based on the 'color' prop
         * - Manages state-specific styling (selected, highlighted, disabled)
         * - Ensures proper contrast and accessibility
         */
        ...getItemStyle({
          isSelected: isSelectedForStyle,
          isKeyboardHighlighted: isKeyboardHighlight,
          color,
          isDisabled,
        }),
      },
      disabled: isDisabled,
      /**
       * Mouse event handlers for precise interaction detection.
       * These prevent keyboard highlight styles from interfering with mouse hover.
       * Essential for maintaining clean visual feedback during mixed input methods.
       */
      onMouseEnter: () => setIsHoveredByMouse(true),
      onMouseLeave: () => setIsHoveredByMouse(false),
    };
  };

  /** SelectAll button variant */
  if (itemType === 'selectAll') {
    return (
      <button
        {...getCommonProps()}
        className={clsx(baseClassName, 'justify-content-between btn-icon-end', `btn-${size}`)}
      >
        {areAllSelected ? <SgFormattedMessage id="picker.deselectAll" /> : <SgFormattedMessage id="picker.selectAll" />}
      </button>
    );
  }

  /** CreateCustom button variant */
  if (itemType === 'createCustom') {
    return (
      <button {...getCommonProps()} className={clsx(baseClassName, 'justify-content-between', `btn-${size}`)}>
        <span>{inputValue}</span>
        <span
          className={clsx(
            'action-text fw-medium font-weight-medium',
            isHighlighted ? 'text-primary' : 'text-secondary',
          )}
        >
          {createCustomItemButtonLabel}
        </span>
      </button>
    );
  }

  /**
   * Regular item variant with complex selection logic.
   * Handles both single and multiple selection modes with proper visual feedback.
   */
  const isSelected =
    itemSelection === 'multiple'
      ? selectedItems.some((selected) => selected.value === item.value)
      : selectedItem?.value === item.value;

  return (
    <button
      className={clsx(
        baseClassName,
        'justify-content-between',
        `btn-${size}`,
        isDisabled && 'disabled text-muted',
        isSelected ? `btn-flat-${color}` : 'btn-flat-primary',
      )}
      {...getCommonProps(isSelected)}
    >
      <div className="flex-grow-1 d-flex align-items-center gap-4px">
        <span className={clsx(isDisabled && 'text-muted')}>{item.highlightNode ?? item.label}</span>
        {item.description && (
          <span className={clsx('text-secondary item__description', isDisabled && 'opacity-50')}>
            • {item.description}
          </span>
        )}
      </div>
      {itemSelection === 'multiple' && !isDisabled && (
        <SgComponentIcon size={size} className="text-secondary" icon={isSelected ? 'remove' : 'add'} />
      )}
    </button>
  );
}
